import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { loadAnalytics, setConsentDefault, setConsentGranted } from "@/lib/analytics";
import { useTr, useLocalizedPath } from "@/i18n";

const KEY = "djmagix-consent";

export function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  const [choice, setChoice] = useState(() => getConsent());
  const tr = useTr();
  const privacyPath = useLocalizedPath("/privacy");

  useEffect(() => {
    const reopen = () => setChoice(null);
    window.addEventListener("dj-reopen-cookies", reopen);
    return () => window.removeEventListener("dj-reopen-cookies", reopen);
  }, []);

  useEffect(() => {
    setConsentDefault(); // Consent Mode v2: tutto negato finché non decidi
    if (choice?.analytics) {
      setConsentGranted();
      loadAnalytics();
    }
  }, [choice]);

  const decide = (analytics) => {
    const value = { analytics, ts: new Date().toISOString(), v: 1 };
    localStorage.setItem(KEY, JSON.stringify(value));
    setChoice(value);
  };

  return (
    <AnimatePresence>
      {choice === null && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4 }}
          className="fixed bottom-6 left-6 right-6 sm:right-auto sm:max-w-md z-[70] gold-frame rounded-2xl bg-[#0D0D0D]/95 backdrop-blur-xl p-6"
        >
          <div className="flex items-center gap-3">
            <Cookie size={20} className="text-[#E2B857] shrink-0" />
            <p className="font-display text-sm font-semibold text-[#F7DE8B] tracking-wide">
              {tr("cookie.title", "Rispettiamo la tua privacy")}
            </p>
          </div>
          <p className="text-xs text-[#E2C882]/80 leading-relaxed mt-3">
            {tr("cookie.text", "Usiamo cookie tecnici e — solo con il tuo consenso — strumenti di analisi (Google Analytics 4 con IP anonimizzato e Microsoft Clarity) per migliorare il sito. Nessuna pubblicità, nessuna vendita di dati. Dettagli nella")}{" "}
            <Link
              to={privacyPath}
              data-testid="cookie-privacy-link"
              className="underline text-[#F7DE8B]"
            >
              Privacy Policy
            </Link>
            .
          </p>
          <div className="flex gap-3 mt-4">
            <button
              data-testid="cookie-accept-button"
              onClick={() => decide(true)}
              className="gold-btn rounded-full px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] flex-1"
            >
              {tr("cookie.accept", "Accetta")}
            </button>
            <button
              data-testid="cookie-reject-button"
              onClick={() => decide(false)}
              className="rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.15em] gold-frame text-[#E2C882] hover:bg-[#E2B857]/10 transition-colors flex-1"
            >
              {tr("cookie.reject", "Rifiuta")}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
